"use client";

import { useRouter } from "next/navigation";

import ArticlesSection from "@/components/brands/ArticlesSection";
import BrandEmptyState from "@/components/brands/BrandEmptyState";
import BrandHeader from "@/components/brands/BrandHeader";
import BrandItemsSection from "@/components/brands/BrandItemsSection";
import ExploreMoreSection from "@/components/brands/ExploreMoreSection";
import { BrandDetailsProvider, useBrandDetails } from "@/contexts/BrandDetailsContext";
import type { BridgeBrandTopCategory } from "@/lib/brand-private-bridge-client";
import { LANDING_PAGE_PATH } from "@/lib/landing-page";
import type { Product } from "@/lib/product-utils";
import type { PublicBrandDetails } from "@/lib/public-shop-agent-fetch";
import { BRAND_EXPLORER_PATH } from "@/lib/route-paths";
import { SECTION_PADDING_X } from "@/styles/responsive-spacing";

interface BrandDetailContentProps {
  brandName: string;
  brandSlug?: string;
  initialBrandDetails: PublicBrandDetails | null;
  initialFeaturedProducts?: Product[] | null;
  initialTopCategories?: BridgeBrandTopCategory[] | null;
}

// Inner component that uses the brand context to determine what to render
function BrandDetailContent({
  brandName,
  brandSlug,
  initialBrandDetails,
  initialFeaturedProducts,
  initialTopCategories,
}: BrandDetailContentProps) {
  const { brandDetails, loading, hasError } = useBrandDetails();
  const router = useRouter();

  // Use context data when available, fall back to initial data
  const effectiveBrandDetails = brandDetails || initialBrandDetails;
  const isLoading = loading && !initialBrandDetails;

  // Show nothing while loading (only if no initial data)
  if (isLoading) {
    return null;
  }

  if (hasError && !effectiveBrandDetails) {
    return (
      <p role="alert" className="p-8 text-body">
        Brand details are temporarily unavailable. Please try again.
      </p>
    );
  }
  if (!effectiveBrandDetails) {
    return (
      <BrandEmptyState
        brandName={brandName}
        onExplore={() => router.push(BRAND_EXPLORER_PATH)}
        onBack={() => router.push(LANDING_PAGE_PATH)}
      />
    );
  }

  // Show the brand content
  return (
    <div className="min-h-screen bg-gray-100">
      <div
        className="mx-auto max-w-6xl py-6 pb-32 md:py-8"
        style={{
          paddingLeft: SECTION_PADDING_X.RESPONSIVE,
          paddingRight: SECTION_PADDING_X.RESPONSIVE,
        }}
      >
        <BrandHeader brandName={brandName} brandSlug={brandSlug} />
        <BrandItemsSection
          brandName={brandName}
          brandSlug={brandSlug}
          initialFeaturedProducts={initialFeaturedProducts}
          initialTopCategories={initialTopCategories}
        />
        <ArticlesSection brandName={brandName} />
        <ExploreMoreSection currentBrandName={brandName} />
      </div>
    </div>
  );
}

interface BrandPageClientProps {
  brandName: string;
  brandSlug?: string;
  initialBrandDetails: PublicBrandDetails | null;
  /** Server-rendered "All" tab products; null/undefined lets the client fetch. */
  initialFeaturedProducts?: Product[] | null;
  /** Server-rendered category tabs; null/undefined lets the client fetch. */
  initialTopCategories?: BridgeBrandTopCategory[] | null;
}

/**
 * Client component for interactive brand page UI.
 * Wrapped by the server component that handles SSR, metadata, and 404s.
 *
 * Accepts initial brand details from server-side fetch to enable
 * immediate rendering without loading state.
 *
 * Note: key={brandName} forces BrandDetailsProvider to remount when navigating
 * between brand pages client-side. Without this, useState initializers only run
 * on mount, causing stale brand data to persist across navigation.
 */
export default function BrandPageClient({
  brandName,
  brandSlug,
  initialBrandDetails,
  initialFeaturedProducts,
  initialTopCategories,
}: BrandPageClientProps) {
  return (
    <BrandDetailsProvider key={brandName} brandName={brandName} initialData={initialBrandDetails}>
      <BrandDetailContent
        brandName={brandName}
        brandSlug={brandSlug}
        initialBrandDetails={initialBrandDetails}
        initialFeaturedProducts={initialFeaturedProducts}
        initialTopCategories={initialTopCategories}
      />
    </BrandDetailsProvider>
  );
}
